import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";

const ROLES = ["owner", "admin", "member", "viewer"];

export default function Team() {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "member" });
  const [open, setOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/members");
    setMembers(data);
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/members", form);
    setForm({ name: "", email: "", role: "member" });
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/members/${id}`);
    load();
  };

  const updateRole = async (m, role) => {
    await api.put(`/members/${m.id}`, { ...m, role });
    load();
  };

  return (
    <div className="p-8 lg:p-12">
      <header className="flex items-end justify-between mb-10">
        <div>
          <div className="label-mono text-green-400 mb-2">// crew.manifest</div>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter" data-testid="team-title">Team</h1>
          <p className="text-neutral-400 text-sm mt-2">Seat assignments. Permissions. The crew.</p>
        </div>
        <button data-testid="team-add-button" onClick={() => setOpen((s) => !s)} className="btn-primary btn-glitch flex items-center gap-2">
          <Plus size={14} /> Seat a player
        </button>
      </header>

      {open && (
        <form onSubmit={submit} data-testid="team-add-form" className="panel panel-accent-green p-6 mb-8 deal">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <label>
              <span className="label-mono block mb-2">Name</span>
              <input data-testid="team-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-shell w-full px-3 py-2 text-sm font-jet" />
            </label>
            <label className="md:col-span-2">
              <span className="label-mono block mb-2">Email</span>
              <input data-testid="team-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-shell w-full px-3 py-2 text-sm font-jet" />
            </label>
            <label>
              <span className="label-mono block mb-2">Role</span>
              <select data-testid="team-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input-shell w-full px-3 py-2 text-sm font-jet">
                {ROLES.map((r) => <option key={r}>{r}</option>)}
              </select>
            </label>
          </div>
          <div className="mt-4 flex gap-3">
            <button type="submit" data-testid="team-submit" className="btn-primary btn-glitch">Add</button>
            <button type="button" data-testid="team-cancel" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      )}

      <div className="panel p-0" data-testid="team-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left label-mono border-b border-neutral-900">
              <th className="px-4 py-3">Player</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-neutral-500 font-jet text-xs">No players seated.</td></tr>
            )}
            {members.map((m, i) => (
              <tr key={m.id} data-testid={`team-row-${m.id}`} className={i % 2 === 0 ? "bg-black" : "bg-neutral-950"}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 border border-neutral-700 bg-neutral-900 flex items-center justify-center font-jet text-amber-400 text-xs">
                      {m.name?.[0]?.toUpperCase()}
                    </div>
                    <span>{m.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-neutral-400 font-jet text-xs">{m.email}</td>
                <td className="px-4 py-3">
                  <select data-testid={`team-role-${m.id}`} value={m.role} onChange={(e) => updateRole(m, e.target.value)} className="input-shell text-xs px-2 py-1 font-jet">
                    {ROLES.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`team-delete-${m.id}`} onClick={() => remove(m.id)} className="text-neutral-500 hover:text-red-400">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
